import React from 'react';
import 'isomorphic-unfetch';
import PropTypes from 'prop-types';
import { AppContext } from '@prisjakt/ui-app-context';

class SearchContainer extends React.Component {
  constructor(props) {
    super(props);

    this.selectionId = 1;

    this.state = {
      queries: {},
      results: null,
      pagingInfo: null,
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      nextProps.query !== this.props.query ||
      nextState.results !== this.state.results ||
      nextProps.pagingInfo !== this.props.pagingInfo
    );
  }

  componentDidMount() {
    this.mounted = true;
    this.search();
  }

  componentWillUnmount() {
    this.mounted = false;
    clearTimeout(this.retrySearch);
  }

  componentDidUpdate() {
    if (!this.props.query) {
      clearTimeout(this.retrySearch);
    }

    this.search();
  }

  addSelectionId(item) {
    return {
      ...item,
      selectionId: `sc${this.selectionId++}`, // eslint-disable-line no-plusplus
    };
  }

  search() {
    const { query, queryOptions, pagingInfo } = this.props;
    const { market } = this.context;

    if (!market || !market.superSearchEndpoint || !market.code) {
      console.error('No superSearchEndpoint is configured in <MarketContext>');
      return;
    }

    if (this.state.queries[query] && this.state.pagingInfo === pagingInfo) {
      this.setState({
        results: this.state.queries[query],
      });
      return;
    }

    const queryGroups = pagingInfo
      ? queryOptions.groups.map(group =>
          Object.assign(group, {
            from: pagingInfo[`${group.name}Offset`] || 0,
          })
        )
      : queryOptions.groups;

    const url = market.superSearchEndpoint;
    const body = {
      market: market.code,
      query,
      groups: queryGroups,
    };

    fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(resp => {
        if (resp.status >= 400 && resp.status < 600) {
          throw resp.message;
        }
        return resp.json();
      })
      .then(json => {
        const groups = json.results.map(group =>
          Object.assign(group, {
            hits: group.hits.map(item => this.addSelectionId(item)),
            offset: (pagingInfo && pagingInfo[`${group.name}Offset`]) || 0,
          })
        );

        const queries = Object.assign(this.state.queries, {
          [query]: groups,
        });

        clearTimeout(this.retrySearch);

        if (this.mounted) {
          requestAnimationFrame(() => {
            this.setState({
              pagingInfo: this.props.pagingInfo,
              queries,
              results:
                body.query === this.props.query ? groups : this.state.results,
            });
          });
        }

        if (!this.props.disableScrollToTop) {
          // Reset scroll position for small devices
          if (IS_BROWSER && window.innerWidth <= 620) {
            window.scrollTo(0, 0);
          }
        }
      })
      .catch(e => {
        console.error(e);

        clearTimeout(this.retrySearch);

        if (this.mounted) {
          // If error occurs, retry search after 1000s
          this.retrySearch = setTimeout(() => {
            this.search();
          }, 1000);
        }
      });
  }

  render() {
    const { results } = this.state;
    if (!results) {
      return this.props.renderLoader();
    }

    if (this.state.error || results.error) {
      return null;
    }

    return this.props.render(results);
  }
}

const {
  bool,
  string,
  number,
  arrayOf,
  objectOf,
  shape,
  func,
  oneOfType,
  object,
} = PropTypes;

SearchContainer.propTypes = {
  render: func.isRequired,
  renderLoader: func,
  query: string.isRequired,
  queryOptions: shape({
    groups: arrayOf(oneOfType([string, object])).isRequired,
  }),
  pagingInfo: objectOf(number),
  disableScrollToTop: bool,
};

SearchContainer.defaultProps = {
  pagingInfo: null,
  queryOptions: {
    groups: [
      {
        limit: 10,
        name: 'categories',
        modes: [
          {
            name: 'brand_category',
            params: {
              fq:
                'row_type:category OR row_type:virtual_category OR row_type:brand_virtual_category',
            },
          },
        ],
      },
      {
        limit: 15,
        name: 'products',
        modes: ['product', 'raw_sorted', 'raw'],
      },
      {
        limit: 10,
        name: 'brands',
        modes: ['brand'],
      },
      {
        limit: 10,
        name: 'stores',
        modes: ['store'],
      },
    ],
  },
  disableScrollToTop: false,
  renderLoader: () => null,
};

SearchContainer.contextType = AppContext;

export default SearchContainer;
